import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Gauge,
  Clock3,
  Sigma,
  ReceiptText,
  Wrench,
  ListChecks,
  AlertCircle,
} from 'lucide-react'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Chip } from './Chip'
import {
  useGetDriverPerformance,
  useGetDriverLastShift,
  useGetDriverShifts,
  useGetDriverRepairs,
  useGetDriverPayslips,
} from '../../hooks/backend/interviews'
import {
  type DriverPerformance,
  type DriverLastShift,
  type DriverShiftRow,
  type DriverRepair,
  type DriverPayslip,
  formatZar,
  formatNum,
  formatDate,
  statusLabel,
  statusTone,
} from '../../utils/interviews'

const TARGET_GROSS = 960

type Timeframe = 'all' | 'week' | 'month' | 'custom'

const TIMEFRAMES: { key: Timeframe; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
]

// Local YYYY-MM-DD (avoids UTC offset shifting the date).
function localIso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function windowLabel(w: string | null): string {
  if (!w) return '—'
  const map: Record<string, string> = { am_pm: 'AM/PM', am: 'AM', pm: 'PM' }
  if (map[w]) return map[w]!
  return w.replace(/_/g, ' ')
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
      {sub != null && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  )
}

function SectionCard({
  icon: Icon,
  title,
  action,
  children,
}: {
  icon: typeof Gauge
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[hsl(var(--brand))]" />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function OnTimeChip({ onTime }: { onTime: boolean }) {
  return onTime ? (
    <Chip tone="green">On time</Chip>
  ) : (
    <Chip tone="red">Late</Chip>
  )
}

export function DriverPerformancePanel({ applicationId }: { applicationId: string }) {
  const [timeframe, setTimeframe] = useState<Timeframe>('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const perf = useGetDriverPerformance()
  const lastShift = useGetDriverLastShift()
  const shifts = useGetDriverShifts()
  const repairs = useGetDriverRepairs()
  const payslips = useGetDriverPayslips()

  const perfTrigger = perf.trigger
  const lastTrigger = lastShift.trigger
  const shiftsTrigger = shifts.trigger
  const repairsTrigger = repairs.trigger
  const payslipsTrigger = payslips.trigger

  // Resolve the active date range for the selected timeframe.
  const range = useMemo<{ from: string | null; to: string | null }>(() => {
    const today = new Date()
    if (timeframe === 'all') return { from: null, to: null }
    if (timeframe === 'week') {
      const start = new Date(today)
      start.setDate(start.getDate() - 6)
      return { from: localIso(start), to: localIso(today) }
    }
    if (timeframe === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      return { from: localIso(start), to: localIso(today) }
    }
    // custom
    return { from: customFrom || null, to: customTo || null }
  }, [timeframe, customFrom, customTo])

  // Custom ranges only query once both ends are set.
  const rangeReady = timeframe !== 'custom' || (Boolean(customFrom) && Boolean(customTo))

  // Timeframe-dependent reads.
  useEffect(() => {
    if (!rangeReady) return
    perfTrigger({ application_id: applicationId, from: range.from, to: range.to }, { skipCache: true })
    shiftsTrigger({ application_id: applicationId, from: range.from, to: range.to }, { skipCache: true })
  }, [applicationId, range.from, range.to, rangeReady, perfTrigger, shiftsTrigger])

  // Timeframe-independent reads.
  useEffect(() => {
    lastTrigger({ application_id: applicationId }, { skipCache: true })
    repairsTrigger({ application_id: applicationId }, { skipCache: true })
    payslipsTrigger({ application_id: applicationId }, { skipCache: true })
  }, [applicationId, lastTrigger, repairsTrigger, payslipsTrigger])

  const perfData = (perf.data as DriverPerformance | null | undefined) ?? null
  const lastData = (lastShift.data as DriverLastShift | null | undefined) ?? null
  const shiftRows = (shifts.data as DriverShiftRow[] | undefined) ?? []
  const repairRows = (repairs.data as DriverRepair[] | undefined) ?? []
  const payslipRows = (payslips.data as DriverPayslip[] | undefined) ?? []

  const anyError =
    perf.error || lastShift.error || shifts.error || repairs.error || payslips.error
  const initialLoading =
    (perf.loading || lastShift.loading || payslips.loading) &&
    perfData == null &&
    lastData == null &&
    payslipRows.length === 0

  const perfShiftCount = perfData ? Number(perfData.shift_count) : 0
  const avgPerShift = perfData?.avg_earnings != null ? Number(perfData.avg_earnings) : null
  const perfTargetLoss = perfData ? Number(perfData.target_loss) : 0

  const sortedShifts = useMemo(
    () =>
      [...shiftRows].sort((a, b) => (a.shift_date < b.shift_date ? 1 : -1)),
    [shiftRows],
  )

  // Current month = 1st of this month (local).
  const firstOfMonth = useMemo(() => {
    const d = new Date()
    return localIso(new Date(d.getFullYear(), d.getMonth(), 1))
  }, [])

  const currentPayslip = useMemo(
    () => payslipRows.find((p) => (p.month ?? '').slice(0, 10) === firstOfMonth) ?? null,
    [payslipRows, firstOfMonth],
  )
  const historicPayslips = useMemo(
    () => payslipRows.filter((p) => (p.month ?? '').slice(0, 10) !== firstOfMonth),
    [payslipRows, firstOfMonth],
  )

  const monthLabel = (m: string) => {
    const d = new Date(m)
    if (Number.isNaN(d.getTime())) return m
    return d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })
  }

  const nothingYet =
    perfData == null && lastData == null && payslipRows.length === 0

  return (
    <section className="space-y-3">
      {/* Header + timeframe selector */}
      <SectionCard
        icon={Gauge}
        title="Performance"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-md bg-muted p-1">
              {TIMEFRAMES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTimeframe(t.key)}
                  className={cn(
                    'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors',
                    timeframe === t.key
                      ? 'bg-background text-foreground shadow-retool-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {timeframe === 'custom' && (
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-9 w-36"
                  aria-label="From date"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-9 w-36"
                  aria-label="To date"
                />
              </div>
            )}
          </div>
        }
      >
        {anyError ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{anyError}</span>
          </div>
        ) : initialLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading performance…
          </div>
        ) : nothingYet ? (
          <p className="py-2 text-sm text-muted-foreground">
            No operational data yet for this driver.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Earnings shown are gross Uber earnings. Take-home is the driver's net.
          </p>
        )}
      </SectionCard>

      {!anyError && !initialLoading && !nothingYet && (
        <>
          {/* Last shift */}
          <SectionCard icon={Clock3} title="Last shift">
            {lastData == null ? (
              <p className="text-sm text-muted-foreground">No shifts recorded yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                <Stat label="Date" value={formatDate(lastData.shift_date)} />
                <Stat label="Earnings" value={formatZar(lastData.earnings)} sub="gross" />
                <Stat label="Take-home" value={formatZar(lastData.take_home)} />
                <Stat
                  label="Petrol used"
                  value={`${formatNum(lastData.petrol_litres)} L`}
                  sub={formatZar(lastData.petrol_cost)}
                />
                <Stat label="Petrol saving" value={formatZar(lastData.petrol_savings)} />
                <Stat label="KM" value={formatNum(lastData.kilometres, 0)} />
                <Stat label="Avg km/L" value={formatNum(lastData.km_per_litre, 1)} />
                <Stat label="Extras" value={formatZar(lastData.extras)} />
                <Stat label="On time" value={<OnTimeChip onTime={lastData.on_time} />} />
                <Stat
                  label="Target loss"
                  value={
                    Number(lastData.target_loss) === 0 ? (
                      <Chip tone="green">On target</Chip>
                    ) : (
                      <span className="text-rose-600 dark:text-rose-400">
                        {formatZar(lastData.target_loss)}
                      </span>
                    )
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Totals for period */}
          <SectionCard icon={Sigma} title="Totals for period">
            {perfData == null || perfShiftCount === 0 ? (
              <p className="text-sm text-muted-foreground">No shifts in this period.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                <Stat label="Shifts" value={perfShiftCount} />
                <Stat label="Gross earnings" value={formatZar(perfData.total_earnings)} />
                <Stat
                  label="Avg per shift"
                  value={
                    <span
                      className={cn(
                        avgPerShift != null && avgPerShift >= TARGET_GROSS
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-amber-600 dark:text-amber-400',
                      )}
                    >
                      {avgPerShift != null ? formatZar(avgPerShift) : '—'}
                    </span>
                  }
                  sub="target R960"
                />
                <Stat label="Take-home" value={formatZar(perfData.take_home)} />
                <Stat
                  label="Petrol"
                  value={`${formatNum(perfData.petrol_litres)} L`}
                  sub={formatZar(perfData.petrol_cost)}
                />
                <Stat label="Petrol saving" value={formatZar(perfData.petrol_savings)} />
                <Stat label="Kilometres" value={formatNum(perfData.kilometres, 0)} />
                <Stat label="Avg km/L" value={formatNum(perfData.km_per_litre, 1)} />
                <Stat label="Extras" value={formatZar(perfData.extras)} />
                <Stat
                  label="On time"
                  value={`${formatNum(perfData.on_time_pct, 1)}%`}
                  sub={`${Number(perfData.on_time_shifts)} / ${perfShiftCount} shifts`}
                />
                <Stat
                  label="Target loss"
                  value={
                    perfTargetLoss === 0 ? (
                      <Chip tone="green">On target</Chip>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">
                        {formatZar(perfTargetLoss)}
                      </span>
                    )
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Shifts table */}
          <SectionCard icon={ListChecks} title="Shifts">
            {sortedShifts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No shifts in this period.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Take-home</TableHead>
                      <TableHead className="text-right">KM</TableHead>
                      <TableHead className="text-right">km/L</TableHead>
                      <TableHead className="text-right">Petrol saving</TableHead>
                      <TableHead className="text-right">Extras</TableHead>
                      <TableHead>On time</TableHead>
                      <TableHead className="text-right">Target loss</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedShifts.map((s) => (
                      <TableRow key={s.shift_id}>
                        <TableCell className="whitespace-nowrap">{formatDate(s.shift_date)}</TableCell>
                        <TableCell className="whitespace-nowrap">{windowLabel(s.shift_window)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(s.earnings)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(s.take_home)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNum(s.kilometres, 0)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatNum(s.km_per_litre, 1)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(s.petrol_savings)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(s.extras)}</TableCell>
                        <TableCell><OnTimeChip onTime={s.on_time} /></TableCell>
                        <TableCell className="text-right tabular-nums">
                          {Number(s.target_loss) === 0 ? (
                            <span className="text-emerald-600 dark:text-emerald-400">On target</span>
                          ) : (
                            formatZar(s.target_loss)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>

          {/* Payslips */}
          <SectionCard icon={ReceiptText} title="Payslips">
            {payslipRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No payslip data yet.</p>
            ) : (
              <div className="space-y-4">
                {currentPayslip ? (
                  <div className="rounded-lg border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] p-4">
                    <p className="mb-2 text-sm font-semibold text-foreground">
                      Current month payslip
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Gross earnings</span>
                        <span className="tabular-nums text-foreground">{formatZar(currentPayslip.gross_earnings)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Extras</span>
                        <span className="tabular-nums text-foreground">{formatZar(currentPayslip.extras)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">less Shortfalls</span>
                        <span className="tabular-nums text-foreground">{formatZar(currentPayslip.shortfalls)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">less Repairs</span>
                        <span className="tabular-nums text-foreground">{formatZar(currentPayslip.repairs)}</span>
                      </div>
                      <div className="mt-1 flex justify-between border-t border-border pt-1.5">
                        <span className="font-semibold text-foreground">Net pay</span>
                        <span className="font-bold tabular-nums text-foreground">{formatZar(currentPayslip.net_pay)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] p-4 text-sm font-medium text-foreground">
                    No shifts yet this month
                  </div>
                )}

                {historicPayslips.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Historic payslips
                    </p>
                    <div className="overflow-x-auto rounded-md border border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">Shifts</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">Net pay</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {historicPayslips.map((p) => (
                            <TableRow key={p.month}>
                              <TableCell className="whitespace-nowrap">{monthLabel(p.month)}</TableCell>
                              <TableCell className="text-right tabular-nums">{Number(p.shift_count)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatZar(p.gross_earnings)}</TableCell>
                              <TableCell className="text-right tabular-nums">{formatZar(p.net_pay)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SectionCard>

          {/* Repairs */}
          <SectionCard icon={Wrench} title="Repairs">
            {repairRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No repair charges.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Raised</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Fault</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repairRows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">{formatDate(r.raised_at)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(r.amount_zar)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatZar(r.outstanding_zar)}</TableCell>
                        <TableCell>
                          {r.status ? (
                            <Chip tone={statusTone(r.status)}>{statusLabel(r.status)}</Chip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {r.fault_attribution || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{r.reason || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </section>
  )
}
